/**
 * Phase 5 — Measurement Books tab (WO detail).
 * Record joint measurements → submit → PM approve (auto-raises WPC).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Loader2, Ruler } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';

const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';
const MB_BADGE = {
  draft: 'bg-slate-100 text-slate-600', submitted: 'bg-amber-50 text-amber-700',
  approved: 'bg-blue-50 text-blue-700', wpc_raised: 'bg-emerald-50 text-emerald-700',
};

export default function WOMeasurementTab({ wo, onChanged }) {
  const [mbs, setMbs] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet('/measurement-books', { work_order_id: wo.id }).then((d) => setMbs(d?.items || [])).catch(() => setMbs([]));
  }, [wo.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (mb, path, body) => {
    setBusy(true);
    try {
      await apiPost(`/measurement-books/${mb.id}/${path}`, body || {});
      toast.success(path === 'approve' ? 'MB approved — WPC raised' : 'Done');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (mbs === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid="wo-mb-tab">
      <div className="flex items-center justify-between">
        <div className="kpi-overline flex items-center gap-1.5"><Ruler size={11} /> Measurement Books ({mbs.length})</div>
        {['approved', 'active'].includes(wo.status) && !creating && (
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="mb-new-btn">
            <Plus size={11} /> New Measurement
          </button>
        )}
      </div>

      {creating && <MBCreateForm wo={wo} onDone={(saved) => { setCreating(false); if (saved) { load(); onChanged?.(); } }} />}

      <table className="w-full dense-table">
        <thead><tr><th>MB No</th><th>Date</th><th>Measured By</th><th className="text-right">This Value</th><th>Status</th><th>WPC</th><th></th></tr></thead>
        <tbody>
          {mbs.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400 text-xs">No measurements recorded</td></tr>}
          {mbs.map((mb) => (
            <React.Fragment key={mb.id}>
              <tr className="cursor-pointer" onClick={() => setExpanded(expanded === mb.id ? null : mb.id)} data-testid={`mb-row-${mb.mb_no}`}>
                <td className="font-mono text-xs">{mb.mb_no}</td>
                <td className="text-xs">{formatDate(mb.measurement_date)}</td>
                <td className="text-xs">{mb.measured_by}</td>
                <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(mb.total_this_value)}</td>
                <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${MB_BADGE[mb.status] || ''}`}>{(mb.status || '').replace('_', ' ')}</span></td>
                <td className="text-xs font-mono">{mb.wpc_id ? '✓' : '—'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    {mb.status === 'draft' && (
                      <button onClick={() => act(mb, 'submit')} disabled={busy} className="px-2 py-1 text-[10px] bg-amber-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`mb-submit-${mb.mb_no}`}>Submit</button>
                    )}
                    {['draft', 'submitted'].includes(mb.status) && (
                      <button onClick={() => {
                        const ld = window.prompt('LD deduction ₹ (0 if none):', '0');
                        if (ld === null) return;
                        act(mb, 'approve', { ld_amount: Number(ld) || 0 });
                      }} disabled={busy} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`mb-approve-${mb.mb_no}`}>Approve → WPC</button>
                    )}
                  </div>
                </td>
              </tr>
              {expanded === mb.id && (
                <tr><td colSpan="7" className="bg-slate-50 px-4 py-3">
                  <table className="w-full dense-table">
                    <thead><tr><th>Item</th><th>UOM</th><th className="text-right">Prev Cum.</th><th className="text-right">This Qty</th><th className="text-right">Cum. To Date</th><th className="text-right">WO Qty</th><th className="text-right">Rate</th><th className="text-right">Value</th></tr></thead>
                    <tbody>
                      {(mb.items || []).map((it) => (
                        <tr key={it.id}>
                          <td className="text-xs">{it.description}</td><td className="text-xs">{it.uom}</td>
                          <td className="text-right tabular-nums text-xs">{it.previous_cumulative_qty}</td>
                          <td className="text-right tabular-nums text-xs font-bold">{it.this_measurement_qty}</td>
                          <td className="text-right tabular-nums text-xs">{it.cumulative_to_date}</td>
                          <td className="text-right tabular-nums text-xs">{it.wo_qty || '—'}</td>
                          <td className="text-right tabular-nums text-xs">{formatINR(it.rate)}</td>
                          <td className="text-right tabular-nums text-xs">{formatINR(it.this_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {mb.witnessed_by && <div className="text-[11px] text-slate-500 mt-2">Witnessed by: {mb.witnessed_by}</div>}
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MBCreateForm({ wo, onDone }) {
  const [items, setItems] = useState([{ description: '', uom: '', this_measurement_qty: '', rate: '', wo_qty: '' }]);
  const [witnessedBy, setWitnessedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const total = items.reduce((s, it) => s + (Number(it.this_measurement_qty) || 0) * (Number(it.rate) || 0), 0);

  const save = async () => {
    const valid = items.filter((it) => it.description && Number(it.this_measurement_qty) > 0);
    if (!valid.length) { toast.error('Add at least one measured item'); return; }
    setSaving(true);
    try {
      await apiPost('/measurement-books', {
        work_order_id: wo.id,
        witnessed_by: witnessedBy,
        items: valid.map((it) => ({
          description: it.description, uom: it.uom,
          this_measurement_qty: Number(it.this_measurement_qty),
          rate: Number(it.rate) || 0, wo_qty: Number(it.wo_qty) || 0,
        })),
      });
      toast.success('Measurement book created (draft)');
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2" data-testid="mb-create-form">
      <div className="kpi-overline">New Measurement — previous cumulative auto-fills from approved MBs</div>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <input placeholder="Item description" value={it.description} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} className={`${inp} flex-1`} data-testid={`mb-item-desc-${i}`} />
          <input placeholder="UOM" value={it.uom} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, uom: e.target.value } : x))} className={`${inp} w-20`} />
          <input placeholder="Qty" type="number" value={it.this_measurement_qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, this_measurement_qty: e.target.value } : x))} className={`${inp} w-24`} data-testid={`mb-item-qty-${i}`} />
          <input placeholder="Rate" type="number" value={it.rate} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} className={`${inp} w-24`} data-testid={`mb-item-rate-${i}`} />
          <input placeholder="WO Qty" type="number" value={it.wo_qty} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, wo_qty: e.target.value } : x))} className={`${inp} w-24`} />
          <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-xs text-red-500 px-1">✕</button>
        </div>
      ))}
      <button onClick={() => setItems([...items, { description: '', uom: '', this_measurement_qty: '', rate: '', wo_qty: '' }])} className="text-xs text-[#3b4872] font-display font-bold">+ Add item</button>
      <div className="flex items-center gap-3">
        <input placeholder="Witnessed by (vendor rep)" value={witnessedBy} onChange={(e) => setWitnessedBy(e.target.value)} className={`${inp} max-w-xs`} />
        <span className="text-xs font-display font-bold ml-auto">Total: {formatINR(total)}</span>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="mb-save-btn">{saving ? 'Saving…' : 'Save Draft'}</button>
        <button onClick={() => onDone(false)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
      </div>
    </div>
  );
}
